import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { HotTable, HotTableRef } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Icon,
  Paper,
  TextField,
  IconButton,
  Fade,
  Chip,
} from '@mui/material';
import { HyperFormula } from 'hyperformula';
import Handsontable from 'handsontable';
import Papa from 'papaparse';

registerAllModules();

const hyperformulaInstance = HyperFormula.buildEmpty({
  licenseKey: 'internal-use-in-handsontable',
});

// Define custom renderer function for highlighting UNKNOWN values and search results
const globalSearchResults: SearchResult[] = [];
let globalSearchIndex = -1;

function highlightUnknownAndSearchRenderer(
  instance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties,
) {
  // Use the default text renderer as a base
  Handsontable.renderers.TextRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  );

  // Reset styles
  td.style.backgroundColor = '';
  td.style.border = '';
  td.style.color = '';
  td.style.fontWeight = '';

  // Apply red styling if the cell value is 'UNKNOWN'
  if (value === 'UNKNOWN') {
    td.style.color = '#c62828';
    td.style.fontWeight = 'bold';
  }

  // Check if this cell is a search result
  const isSearchResult = globalSearchResults.some(
    (result) => result.row === row && result.col === col,
  );

  if (isSearchResult) {
    const resultIndex = globalSearchResults.findIndex(
      (result) => result.row === row && result.col === col,
    );

    if (resultIndex === globalSearchIndex) {
      // Current/active search result - bright highlight
      td.style.backgroundColor = '#ffeb3b';
      td.style.border = '2px solid #f57f17';
    } else {
      // Other search results - subtle highlight
      td.style.backgroundColor = '#fff3c4';
      td.style.border = '1px solid #fbc02d';
    }
  }
}

// Register the custom renderer
Handsontable.renderers.registerRenderer(
  'highlightUnknownAndSearch',
  highlightUnknownAndSearchRenderer,
);

interface SelectionSummary {
  sum: number;
  count: number;
  average: number;
  min: number;
  max: number;
  numericCount: number;
}

interface SearchResult {
  row: number;
  col: number;
  value: string;
}

interface SpreadsheetViewerProps {
  data: unknown[][];
  loading?: boolean;
  error?: string | null;
  height?: string | number;
  colHeaders?: boolean | string[];
  rowHeaders?: boolean;
  afterGetColHeader?: (col: number, TH: HTMLElement) => void;
  id?: string;
  showSelectionSummary?: boolean;
  onDataChange?: (data: unknown[][]) => void;
  filename?: string;
  readOnly?: boolean;
}

export interface SpreadsheetViewerRef extends HotTableRef {
  getSortedData: () => unknown[][];
}

const SpreadsheetViewer = React.forwardRef<
  SpreadsheetViewerRef,
  SpreadsheetViewerProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      height = '100%',
      colHeaders = true,
      rowHeaders = true,
      afterGetColHeader,
      id,
      showSelectionSummary = false,
      onDataChange,
      filename,
      readOnly = false,
    },
    ref,
  ) => {
    // Process data and headers
    const processedData = React.useMemo(() => {
      if (!data || data.length === 0) {
        return { tableData: data || [], headers: colHeaders };
      }

      // If colHeaders is true and we have data, extract first row as headers
      if (colHeaders === true && data.length > 0) {
        const headers = data[0].map((cell) => String(cell || ''));
        const tableData = data.slice(1); // Remove first row from data
        return { tableData, headers };
      }

      // If colHeaders is an array or false, use data as-is
      return { tableData: data, headers: colHeaders };
    }, [data, colHeaders]);

    // Generate columns configuration with the custom renderer for all columns
    const columnsConfig = React.useMemo(() => {
      if (!processedData.tableData.length) return [];

      const columnCount = processedData.tableData[0]?.length || 0;
      return Array.from({ length: columnCount }, () => ({
        renderer: 'highlightUnknownAndSearch',
        readOnly: readOnly,
      }));
    }, [processedData.tableData, readOnly]);

    // Selection summary state
    const [selectionSummary, setSelectionSummary] =
      useState<SelectionSummary | null>(null);

    // Search state
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
    const [caseSensitive, setCaseSensitive] = useState(false);

    // Removed isTableFocused state - let Handsontable handle its own focus

    // Zoom state
    const [zoomLevel, setZoomLevel] = useState(100); // Percentage

    const hotTableRef = useRef<HotTableRef>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Method to get sorted data on demand
    const getSortedData = useCallback((): unknown[][] => {
      if (!hotTableRef.current) return data;

      try {
        const hotInstance = hotTableRef.current.hotInstance;
        if (!hotInstance) return data;

        // Get the sorted data from the table
        const sortedTableData = hotInstance.getData();

        // If we extracted headers originally, reconstruct the full data array
        if (colHeaders === true && processedData.headers) {
          return [processedData.headers, ...sortedTableData];
        } else {
          // Otherwise, just use the sorted table data
          return sortedTableData;
        }
      } catch (error) {
        console.warn('Error getting sorted data:', error);
        return data;
      }
    }, [data, colHeaders, processedData.headers]);

    // Search functionality
    const performSearch = useCallback(
      (term: string, isCaseSensitive = false, autoNavigate = false) => {
        if (!term.trim() || !hotTableRef.current?.hotInstance) {
          setSearchResults([]);
          setCurrentSearchIndex(-1);
          return;
        }

        const hotInstance = hotTableRef.current.hotInstance;
        const tableData = hotInstance.getData();
        const results: SearchResult[] = [];

        const searchValue = isCaseSensitive ? term : term.toLowerCase();

        tableData.forEach((row: unknown[], rowIndex: number) => {
          row.forEach((cell: unknown, colIndex: number) => {
            if (cell !== null && cell !== undefined) {
              const cellValue = String(cell);
              const compareValue = isCaseSensitive
                ? cellValue
                : cellValue.toLowerCase();

              if (compareValue.includes(searchValue)) {
                results.push({
                  row: rowIndex,
                  col: colIndex,
                  value: cellValue,
                });
              }
            }
          });
        });

        setSearchResults(results);
        // Set to -1 when not auto-navigating, 0 when auto-navigating
        const newIndex = autoNavigate && results.length > 0 ? 0 : -1;
        setCurrentSearchIndex(newIndex);

        // Update global variables for renderer
        globalSearchResults.length = 0;
        globalSearchResults.push(...results);
        globalSearchIndex = newIndex;

        // Force re-render to show highlights
        if (hotTableRef.current?.hotInstance) {
          hotTableRef.current.hotInstance.render();
        }

        // Only navigate to first result if explicitly requested
        if (autoNavigate && results.length > 0) {
          navigateToSearchResult(0, results);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const navigateToSearchResult = useCallback(
      (index: number, results: SearchResult[] = searchResults) => {
        if (
          index < 0 ||
          index >= results.length ||
          !hotTableRef.current?.hotInstance
        )
          return;

        const result = results[index];
        const hotInstance = hotTableRef.current.hotInstance;

        // Only scroll to the cell, don't select it to keep focus in search input
        hotInstance.scrollViewportTo(result.row, result.col);

        // Update global variables for renderer
        globalSearchIndex = index;

        // Force re-render to update highlighting
        hotInstance.render();
      },
      [searchResults],
    );

    const handleSearchNext = useCallback(() => {
      if (searchResults.length === 0) return;

      // If we haven't navigated yet (currentSearchIndex === -1), go to first result
      const nextIndex =
        currentSearchIndex === -1
          ? 0
          : (currentSearchIndex + 1) % searchResults.length;
      setCurrentSearchIndex(nextIndex);
      navigateToSearchResult(nextIndex);
    }, [currentSearchIndex, searchResults, navigateToSearchResult]);

    const handleSearchPrevious = useCallback(() => {
      if (searchResults.length === 0) return;

      // If we haven't navigated yet (currentSearchIndex === -1), go to last result
      const prevIndex =
        currentSearchIndex === -1
          ? searchResults.length - 1
          : currentSearchIndex <= 0
            ? searchResults.length - 1
            : currentSearchIndex - 1;
      setCurrentSearchIndex(prevIndex);
      navigateToSearchResult(prevIndex);
    }, [currentSearchIndex, searchResults, navigateToSearchResult]);

    const handleSearchChange = useCallback(
      (value: string) => {
        setSearchTerm(value);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        // If empty search, clear results immediately
        if (!value.trim()) {
          setSearchResults([]);
          setCurrentSearchIndex(-1);
          globalSearchResults.length = 0;
          globalSearchIndex = -1;
          if (hotTableRef.current?.hotInstance) {
            hotTableRef.current.hotInstance.render();
          }
          return;
        }

        // Debounce the actual search
        searchTimeoutRef.current = setTimeout(() => {
          performSearch(value, caseSensitive, false);
        }, 300); // 300ms delay
      },
      [performSearch, caseSensitive],
    );

    // Clean up timeout on unmount
    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    const handleCloseSearch = useCallback(() => {
      // Clear any pending search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      setShowSearch(false);
      setSearchTerm('');
      setSearchResults([]);
      setCurrentSearchIndex(-1);

      // Clear global variables for renderer
      globalSearchResults.length = 0;
      globalSearchIndex = -1;

      // Force re-render to remove highlights
      if (hotTableRef.current?.hotInstance) {
        hotTableRef.current.hotInstance.render();
      }

      // Return focus to the table
      if (hotTableRef.current?.hotInstance) {
        hotTableRef.current.hotInstance.listen();
      }
    }, []);

    // Removed problematic table focus handlers that interfered with Handsontable editing

    // Zoom control handlers
    const handleZoomIn = useCallback(() => {
      setZoomLevel((prev) => Math.min(prev + 10, 200)); // Max 200%
    }, []);

    const handleZoomOut = useCallback(() => {
      setZoomLevel((prev) => Math.max(prev - 10, 50)); // Min 50%
    }, []);

    const handleZoomReset = useCallback(() => {
      setZoomLevel(100);
    }, []);

    // Apply zoom to table container
    const tableContainerStyle = React.useMemo(
      () => ({
        outline: 'none',
        height: '100%',
        width: '100%',
        border: showSearch ? '2px solid #1976d2' : '2px solid transparent',
        borderRadius: '4px',
        transition: 'border-color 0.2s ease-in-out',
        overflow: 'hidden', // Prevent scrollbars from zoom
      }),
      [showSearch],
    );

    const tableStyle = React.useMemo(
      () => ({
        height: `${100 / (zoomLevel / 100)}%`, // Scale container to fill space
        width: `${100 / (zoomLevel / 100)}%`,
        transform: `scale(${zoomLevel / 100})`,
        transformOrigin: 'top left',
      }),
      [zoomLevel],
    );

    // Keyboard shortcuts - only handle when component (table or search bar) is focused
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Don't interfere if user is typing in an input field or editing a cell
        const target = event.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        // Only respond to shortcuts when focus is within this component (table or search bar)
        const activeElement = document.activeElement as HTMLElement | null;
        const isActiveInSearchBar =
          !!activeElement?.closest('[data-search-bar]');
        const isActiveInTable = !!(
          activeElement && tableContainerRef.current?.contains(activeElement)
        );
        if (!isActiveInSearchBar && !isActiveInTable) {
          return;
        }

        // Check if Handsontable is in edit mode
        if (hotTableRef.current?.hotInstance) {
          const activeEditor =
            hotTableRef.current.hotInstance.getActiveEditor();
          if (activeEditor && activeEditor.isOpened()) {
            return; // Don't interfere with cell editing
          }
        }

        // Toggle search with Ctrl+F
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
          event.preventDefault();

          if (showSearch) {
            handleCloseSearch();
          } else {
            // Deselect any active cell in Handsontable first
            if (hotTableRef.current?.hotInstance) {
              hotTableRef.current.hotInstance.deselectCell();
            }

            setShowSearch(true);
            // Focus immediately without delay to prevent keystrokes from going to cells
            // Use requestAnimationFrame to ensure the search input is rendered first
            requestAnimationFrame(() => {
              searchInputRef.current?.focus();
              searchInputRef.current?.select();
            });
          }
        }

        // Zoom shortcuts
        if (event.ctrlKey || event.metaKey) {
          if (event.key === '=' || event.key === '+') {
            event.preventDefault();
            handleZoomIn();
          } else if (event.key === '-') {
            event.preventDefault();
            handleZoomOut();
          } else if (event.key === '0') {
            event.preventDefault();
            handleZoomReset();
          }
        }

        // Escape to close search
        if (event.key === 'Escape' && showSearch) {
          handleCloseSearch();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [
      showSearch,
      handleCloseSearch,
      handleZoomIn,
      handleZoomOut,
      handleZoomReset,
    ]);

    // Auto-focus search input when search bar becomes visible
    useEffect(() => {
      if (showSearch && searchInputRef.current) {
        // Don't deselect cells - let user keep their selection while searching
        // Focus the search input
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }
    }, [showSearch]);

    // Handle case sensitivity toggle
    const handleCaseSensitiveToggle = useCallback(() => {
      const newCaseSensitive = !caseSensitive;
      setCaseSensitive(newCaseSensitive);

      // If there's a current search term, re-search immediately with new case sensitivity
      if (searchTerm.trim()) {
        // Clear existing timeout to avoid conflict
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        // Immediate search when toggling case sensitivity (no debounce needed)
        performSearch(searchTerm, newCaseSensitive, false);
      }
    }, [caseSensitive, searchTerm, performSearch]);

    // Handle clicks outside table to close search
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const isClickingOnTable = tableContainerRef.current?.contains(target);
        const isClickingOnSearchBar = (target as Element)?.closest(
          '[data-search-bar]',
        );

        if (!isClickingOnTable && !isClickingOnSearchBar && showSearch) {
          handleCloseSearch();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [showSearch, handleCloseSearch]);

    // Calculate aggregations for selected cells
    const calculateSelectionSummary = useCallback(
      (selectedData: unknown[][]): SelectionSummary => {
        const flatData = selectedData.flat();
        const numericValues: number[] = [];

        // Extract numeric values
        flatData.forEach((value) => {
          if (value !== null && value !== undefined && value !== '') {
            const numValue =
              typeof value === 'number' ? value : parseFloat(String(value));
            if (!isNaN(numValue) && isFinite(numValue)) {
              numericValues.push(numValue);
            }
          }
        });

        const sum = numericValues.reduce((acc, val) => acc + val, 0);
        const count = flatData.filter(
          (val) => val !== null && val !== undefined && val !== '',
        ).length;
        const numericCount = numericValues.length;
        const average = numericCount > 0 ? sum / numericCount : 0;
        const min = numericCount > 0 ? Math.min(...numericValues) : 0;
        const max = numericCount > 0 ? Math.max(...numericValues) : 0;

        return {
          sum,
          count,
          average,
          min,
          max,
          numericCount,
        };
      },
      [],
    );

    // Handle selection changes
    const handleAfterSelection = useCallback(
      (row: number, column: number, row2: number, column2: number) => {
        if (!showSelectionSummary || !hotTableRef.current) return;

        try {
          const hotInstance = hotTableRef.current?.hotInstance;
          if (!hotInstance) return;

          const startRow = Math.min(row, row2);
          const endRow = Math.max(row, row2);
          const startCol = Math.min(column, column2);
          const endCol = Math.max(column, column2);

          const selectedData = hotInstance.getData(
            startRow,
            startCol,
            endRow,
            endCol,
          );
          const summary = calculateSelectionSummary(selectedData);
          setSelectionSummary(summary);
        } catch (error) {
          console.warn('Error calculating selection summary:', error);
          setSelectionSummary(null);
        }
      },
      [showSelectionSummary, calculateSelectionSummary],
    );

    // Handle deselection
    const handleAfterDeselect = useCallback(() => {
      if (showSelectionSummary) {
        setSelectionSummary(null);
      }
    }, [showSelectionSummary]);

    // Handle column sorting - no immediate updates to prevent rerenders
    const handleAfterColumnSort = useCallback(
      (currentSortConfig: unknown, destinationSortConfigs: unknown) => {
        // Log for debugging - this should not cause rerenders
        console.log(
          'Column sorted:',
          currentSortConfig,
          'New sort configs:',
          destinationSortConfigs,
        );

        // Don't call setData immediately to avoid performance issues
        // The sorted data can be accessed via getSortedData() when needed (e.g., for downloads)
      },
      [],
    );

    // Handle data changes
    const handleAfterChange = useCallback(
      (changes: Handsontable.CellChange[] | null, source: string) => {
        if (source === 'edit' && onDataChange && hotTableRef.current) {
          // User edited cells, get the current data and notify parent
          const currentData = hotTableRef.current.hotInstance?.getData() || [];
          // If we extracted headers originally, reconstruct the full data array
          if (colHeaders === true && processedData.headers) {
            const fullData = [processedData.headers, ...currentData];
            onDataChange(fullData);
          } else {
            onDataChange(currentData);
          }
        }
      },
      [onDataChange, colHeaders, processedData.headers],
    );

    // Forward ref to include both HotTable instance and getSortedData method
    useImperativeHandle(
      ref,
      // @ts-expect-error
      () => ({
        ...hotTableRef.current,
        getSortedData,
      }),
      [getSortedData],
    );

    const hotTable = React.useMemo(
      () => (
        <div ref={tableContainerRef} style={tableContainerStyle}>
          <div style={tableStyle}>
            <HotTable
              ref={hotTableRef}
              data={processedData.tableData}
              colHeaders={processedData.headers}
              rowHeaders={rowHeaders}
              height="100%"
              width="100%"
              licenseKey="non-commercial-and-evaluation"
              stretchH="all"
              autoWrapRow={true}
              autoWrapCol={true}
              rowHeights={24}
              contextMenu={readOnly ? false : true}
              columnSorting={true}
              manualColumnResize={true}
              manualRowResize={true}
              columns={columnsConfig}
              formulas={{
                engine: hyperformulaInstance,
                sheetName: 'Sheet1',
              }}
              afterGetColHeader={afterGetColHeader}
              afterSelection={
                showSelectionSummary ? handleAfterSelection : undefined
              }
              afterDeselect={
                showSelectionSummary ? handleAfterDeselect : undefined
              }
              afterColumnSort={handleAfterColumnSort}
              afterChange={readOnly ? undefined : handleAfterChange}
              // Fix editing UX issues - let Handsontable handle editing naturally
              enterBeginsEditing={readOnly ? false : true}
              enterMoves={readOnly ? undefined : { row: 1, col: 0 }}
              tabMoves={readOnly ? undefined : { row: 0, col: 1 }}
              outsideClickDeselects={false} // Keep selection when clicking outside
            />
          </div>
        </div>
      ),
      [
        tableContainerStyle,
        tableStyle,
        processedData.tableData,
        processedData.headers,
        rowHeaders,
        readOnly,
        columnsConfig,
        afterGetColHeader,
        showSelectionSummary,
        handleAfterSelection,
        handleAfterDeselect,
        handleAfterColumnSort,
        handleAfterChange,
      ],
    );
    if (loading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={height}
        >
          <CircularProgress color="info" />
        </Box>
      );
    }
    if (error) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height={height}
          p={3}
          textAlign="center"
        >
          <Icon color="error" sx={{ fontSize: 48, mb: 2 }}>
            error_outline
          </Icon>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Box>
      );
    }

    // Search bar component
    const searchBarComponent = (
      <Fade in={showSearch}>
        <Paper
          data-search-bar
          elevation={4}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1001,
            p: 2,
            minWidth: 320,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              inputRef={searchInputRef}
              size="small"
              placeholder="Search in spreadsheet..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchTerm) {
                  e.preventDefault();
                  if (e.shiftKey) {
                    handleSearchPrevious();
                  } else {
                    handleSearchNext();
                  }
                }
              }}
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <Icon sx={{ mr: 1, color: 'text.secondary' }}>search</Icon>
                ),
              }}
            />

            {searchResults.length > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  size="small"
                  label={
                    currentSearchIndex === -1
                      ? `0/${searchResults.length}`
                      : `${currentSearchIndex + 1}/${searchResults.length}`
                  }
                  variant="outlined"
                />
                <IconButton
                  size="small"
                  onClick={handleSearchPrevious}
                  disabled={searchResults.length === 0}
                >
                  <Icon>keyboard_arrow_up</Icon>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleSearchNext}
                  disabled={searchResults.length === 0}
                >
                  <Icon>keyboard_arrow_down</Icon>
                </IconButton>
              </Box>
            )}

            <IconButton
              size="small"
              onClick={handleCaseSensitiveToggle}
              color={caseSensitive ? 'primary' : 'default'}
              title="Case sensitive"
            >
              <Icon>text_fields</Icon>
            </IconButton>

            <IconButton size="small" onClick={handleCloseSearch}>
              <Icon>close</Icon>
            </IconButton>
          </Box>

          {searchTerm && searchResults.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              No results found
            </Typography>
          )}
        </Paper>
      </Fade>
    );

    // Footer component with zoom controls and selection summary
    const footerComponent = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          minHeight: '48px',
        }}
      >
        {/* Left side - Zoom controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 50}
            title="Zoom Out (Ctrl/Cmd + -)"
          >
            <Icon>zoom_out</Icon>
          </IconButton>

          <Typography
            variant="body2"
            sx={{
              minWidth: '60px',
              textAlign: 'center',
              fontWeight: 500,
              cursor: 'pointer',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onClick={handleZoomReset}
            title="Reset Zoom (Ctrl/Cmd + 0)"
          >
            {zoomLevel}%
          </Typography>

          <IconButton
            size="small"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 200}
            title="Zoom In (Ctrl/Cmd + +)"
          >
            <Icon>zoom_in</Icon>
          </IconButton>
        </Box>

        {/* Right side - Download button and selection summary */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* Download button */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              try {
                const dataToDownload = getSortedData();

                // Generate CSV content
                const csvContent = Papa.unparse(dataToDownload);

                // Create filename with timestamp
                const timestamp = new Date().toISOString().split('T')[0];
                const baseFilename = filename
                  ? filename.replace(/\.[^/.]+$/, '') // Remove extension
                  : 'spreadsheet-data';
                const downloadFilename = `${baseFilename}-${timestamp}.csv`;

                // Create and trigger download
                const blob = new Blob([csvContent], {
                  type: 'text/csv;charset=utf-8;',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = downloadFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.warn('Error downloading data:', error);
              }
            }}
            title="Download CSV"
            sx={{
              borderColor: 'info.main',
              color: 'info.main',
              backgroundColor: 'white',
              minWidth: 'auto',
              px: 1,
              '&:hover': {
                borderColor: 'info.main',
                backgroundColor: 'info.light',
              },
            }}
          >
            <Icon>download</Icon>
          </Button>

          {showSelectionSummary && selectionSummary ? (
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Count: <strong>{selectionSummary.count}</strong>
              </Typography>
              {selectionSummary.numericCount > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Sum:{' '}
                    <strong>{selectionSummary.sum.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: <strong>{selectionSummary.average.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Min:{' '}
                    <strong>{selectionSummary.min.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Max:{' '}
                    <strong>{selectionSummary.max.toLocaleString()}</strong>
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            // Empty space to maintain layout balance
            <Box />
          )}
        </Box>
      </Box>
    );

    // Combine table with overlays and footer
    const tableWithOverlaysAndFooter = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        {/* Table area with search overlay */}
        <Box
          sx={{
            position: 'relative',
            flexGrow: 1,
            minHeight: 0, // Important for flex child to shrink
            overflow: 'hidden',
          }}
        >
          {hotTable}
          {searchBarComponent}
        </Box>

        {/* Fixed footer */}
        {footerComponent}
      </Box>
    );

    // If id is provided, wrap in a div for compatibility
    if (id) {
      return (
        <div id={id} style={{ width: '100%', height: '100%' }}>
          {tableWithOverlaysAndFooter}
        </div>
      );
    }

    return tableWithOverlaysAndFooter;
  },
);

SpreadsheetViewer.displayName = 'SpreadsheetViewer';

export type { SpreadsheetViewerProps };
export default SpreadsheetViewer;
